import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const inp = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const lbl = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '14px',
  color: '#444',
  fontWeight: 600
};

const ROLE_MAP = {
  'system admin': '2', 'system_admin': '2',
  'tca': '3',
  'invigilator': '4',
  'attender': '5'
};

const AddUser = () => {

  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser } = useAuth();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    phone: '',
    department: '',
    is_active: 1
  });

  useEffect(() => {
    if (isEdit) fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`/users/${id}`);
      const u = res.data;
      setForm({
        name: u.name || '',
        email: u.email || '',
        password: '',
        role_id: u.role_id || '',
        phone: u.phone || '',
        department: u.department || '',
        is_active: u.is_active === 0 ? 0 : 1
      });
    } catch (err) {
      console.log(err);
      toast.error('Failed to load user');
      navigate('/users');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (status) => {
    setForm((prev) => ({ ...prev, is_active: status }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.role_id) {
      toast.error('Please select a role');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        role_id: Number(form.role_id),
        is_active: Number(form.is_active)
      };
      if (isEdit && !payload.password) delete payload.password;
      if (isEdit) {
        await axios.put(`/users/${id}`, payload);
        toast.success('User updated successfully!');
      } else {
        await axios.post('/users', payload);
        toast.success('User created successfully!');
      }
      navigate('/users');
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || (isEdit ? 'Update failed' : 'Create failed'));
    } finally {
      setLoading(false);
    }
  };

  const roleOptions =
    currentUser?.role === 'super_admin'
      ? [
          { v: '2', l: 'System Admin' },
          { v: '3', l: 'TCA' },
          { v: '4', l: 'Invigilator' },
          { v: '5', l: 'Attender' }
        ]
      : [
          { v: '3', l: 'TCA' },
          { v: '4', l: 'Invigilator' },
          { v: '5', l: 'Attender' }
        ];

  // ─── BULK UPLOAD HELPERS ───────────────────────────────────────────────────

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['name', 'email', 'password', 'role', 'phone', 'department'],
      ['John Doe', 'john@example.com', 'pass123', 'Invigilator', '9876543210', 'Science'],
      ['Jane Smith', 'jane@example.com', 'pass456', 'TCA', '9123456780', 'Math']
    ]);
    ws['!cols'] = [{ wch: 20 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'bulk_users_template.xlsx');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

        const parsed = [];
        const errors = [];

        rows.forEach((row, i) => {
          const rowNum = i + 2; // Excel row (1-indexed + header)
          const name = String(row['name'] || '').trim();
          const email = String(row['email'] || '').trim();
          const password = String(row['password'] || '').trim();
          const roleRaw = String(row['role'] || '').trim().toLowerCase();
          const phone = String(row['phone'] || '').trim();
          const department = String(row['department'] || '').trim();

          const rowErrors = [];
          if (!name) rowErrors.push('Name missing');
          if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) rowErrors.push('Invalid email');
          if (!password) rowErrors.push('Password missing');

          const role_id = ROLE_MAP[roleRaw];
          if (!role_id) rowErrors.push(`Unknown role "${row['role']}"`);

          // If system_admin not allowed for current user
          if (role_id === '2' && currentUser?.role !== 'super_admin') {
            rowErrors.push('Not allowed to create System Admin');
          }

          if (rowErrors.length > 0) {
            errors.push({ row: rowNum, errors: rowErrors });
          } else {
            parsed.push({ name, email, password, role_id, phone, department, is_active: 1 });
          }
        });

        setBulkData(parsed);
        setBulkErrors(errors);

        if (errors.length > 0) {
          toast.warn(`${errors.length} row(s) have errors. Fix them and re-upload.`);
        } else if (parsed.length === 0) {
          toast.error('No valid rows found in the file.');
        } else {
          toast.success(`${parsed.length} user(s) ready to import.`);
        }
      } catch (err) {
        console.log(err);
        toast.error('Failed to read Excel file. Ensure it matches the template.');
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ''; // reset so same file can be re-uploaded
  };

  const handleBulkSubmit = async () => {
    if (bulkData.length === 0) return;
    setBulkLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const user of bulkData) {
      try {
        await axios.post('/users', { ...user, role_id: Number(user.role_id) });
        successCount++;
      } catch (err) {
        failCount++;
      }
    }

    setBulkLoading(false);
    if (successCount > 0) toast.success(`${successCount} user(s) created successfully!`);
    if (failCount > 0) toast.error(`${failCount} user(s) failed (possibly duplicate emails).`);
    if (successCount > 0) navigate('/users');
  };

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '32px', backgroundColor: '#f5f6fa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>

        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <button
            onClick={() => navigate('/users')}
            style={{
              background: 'none', border: '1px solid #ddd', padding: '6px 14px',
              borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#555'
            }}
          >
            ← Back
          </button>
          <h2 style={{ color: '#f1870e', margin: 0, fontSize: '22px', fontWeight: 700 }}>
            {isEdit ? 'Edit User' : 'Add New User'}
          </h2>
        </div>

        {/* MODE TOGGLE (only on Add, not Edit) */}
        {!isEdit && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <button
              onClick={() => setBulkMode(false)}
              style={{
                padding: '9px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '14px',
                background: !bulkMode ? '#f1870e' : '#e9e9e9',
                color: !bulkMode ? 'white' : '#555'
              }}
            >
              Single User
            </button>
            <button
              onClick={() => setBulkMode(true)}
              style={{
                padding: '9px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '14px',
                background: bulkMode ? '#f1870e' : '#e9e9e9',
                color: bulkMode ? 'white' : '#555'
              }}
            >
              Bulk Upload (Excel)
            </button>
          </div>
        )}

        {/* ── BULK UPLOAD PANEL ── */}
        {!isEdit && bulkMode && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '32px' }}>

            {/* Step 1: Download template */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{ ...lbl, marginBottom: '10px' }}>Step 1 — Download the Excel template</p>
              <button
                onClick={downloadTemplate}
                style={{
                  padding: '9px 18px', backgroundColor: '#1565c0', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px'
                }}
              >
                ⬇ Download Template
              </button>
              <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                Columns: name, email, password, role (Invigilator / TCA / Attender
                {currentUser?.role === 'super_admin' ? ' / System Admin' : ''}), phone, department
              </p>
            </div>

            {/* Step 2: Upload filled file */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{ ...lbl, marginBottom: '10px' }}>Step 2 — Upload the filled Excel file</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current.click()}
                style={{
                  padding: '9px 18px', backgroundColor: '#388e3c', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px'
                }}
              >
                📂 Choose Excel File
              </button>
            </div>

            {/* Validation errors */}
            {bulkErrors.length > 0 && (
              <div style={{ marginBottom: '20px', backgroundColor: '#fff3e0', borderRadius: '8px', padding: '16px', border: '1px solid #ffe0b2' }}>
                <p style={{ fontWeight: 700, color: '#e65100', marginBottom: '10px', fontSize: '14px' }}>
                  ⚠ {bulkErrors.length} row(s) with errors (fix in file and re-upload):
                </p>
                {bulkErrors.map((e, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                    <strong>Row {e.row}:</strong> {e.errors.join(', ')}
                  </div>
                ))}
              </div>
            )}

            {/* Preview table */}
            {bulkData.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontWeight: 700, color: '#2e7d32', fontSize: '14px', marginBottom: '10px' }}>
                  ✓ {bulkData.length} user(s) ready to import
                </p>
                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #eee' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f6fa' }}>
                        {['Name', 'Email', 'Role', 'Phone', 'Department'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#444', borderBottom: '1px solid #eee' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bulkData.map((u, i) => {
                        const roleLabel = roleOptions.find(r => r.v === u.role_id)?.l || u.role_id;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '9px 12px' }}>{u.name}</td>
                            <td style={{ padding: '9px 12px' }}>{u.email}</td>
                            <td style={{ padding: '9px 12px' }}>{roleLabel}</td>
                            <td style={{ padding: '9px 12px' }}>{u.phone || '—'}</td>
                            <td style={{ padding: '9px 12px' }}>{u.department || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Import button */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                onClick={handleBulkSubmit}
                disabled={bulkData.length === 0 || bulkLoading}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#f1870e', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700,
                  cursor: bulkData.length === 0 || bulkLoading ? 'not-allowed' : 'pointer',
                  opacity: bulkData.length === 0 || bulkLoading ? 0.6 : 1
                }}
              >
                {bulkLoading ? 'Importing...' : `Import ${bulkData.length} User(s)`}
              </button>
              <button
                onClick={() => navigate('/users')}
                style={{
                  padding: '12px 24px', backgroundColor: 'white', color: '#666',
                  border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>

          </div>
        )}

        {/* ── SINGLE USER FORM (original, untouched) ── */}
        {(isEdit || !bulkMode) && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '32px' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>Full Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} required placeholder="Enter full name" style={inp} />
                </div>

                <div>
                  <label style={lbl}>Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="Enter email" style={inp} />
                </div>

                <div>
                  <label style={lbl}>{isEdit ? 'New Password (Optional)' : 'Password *'}</label>
                  <input
                    name="password" type="password" value={form.password} onChange={handleChange}
                    required={!isEdit} placeholder={isEdit ? 'Leave blank' : 'Enter password'} style={inp}
                  />
                </div>

                <div>
                  <label style={lbl}>Role *</label>
                  <select name="role_id" value={form.role_id} onChange={handleChange} style={{ ...inp, backgroundColor: 'white' }}>
                    <option value="">Select Role</option>
                    {roleOptions.map((r) => (
                      <option key={r.v} value={r.v}>{r.l}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={lbl}>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="Enter phone number" style={inp} />
                </div>

                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>Department</label>
                  <input name="department" value={form.department} onChange={handleChange} placeholder="Enter department" style={inp} />
                </div>

                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>Account Status</label>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <button
                      type="button" onClick={() => handleStatusChange(1)}
                      style={{
                        padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600',
                        background: form.is_active === 1 ? '#2e7d32' : '#e8f5e9',
                        color: form.is_active === 1 ? 'white' : '#2e7d32'
                      }}
                    >Active</button>
                    <button
                      type="button" onClick={() => handleStatusChange(0)}
                      style={{
                        padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600',
                        background: form.is_active === 0 ? '#c62828' : '#ffebee',
                        color: form.is_active === 0 ? 'white' : '#c62828'
                      }}
                    >Deactivated</button>
                  </div>
                </div>

              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                <button
                  type="submit" disabled={loading}
                  style={{
                    flex: 1, padding: '12px', backgroundColor: '#f1870e', color: 'white',
                    border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create User')}
                </button>
                <button
                  type="button" onClick={() => navigate('/users')}
                  style={{
                    padding: '12px 24px', backgroundColor: 'white', color: '#666',
                    border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
    </div>
  );
};

export default AddUser;